import { UseQueryResult } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { createColumnHelper } from "@tanstack/table-core";
import { useCallback, useContext, useMemo, useState } from "react";
import {
  Alert,
  Button,
  ButtonGroup,
  ButtonToolbar,
  Col,
  Form,
  Row,
} from "react-bootstrap";
import type { SerializedObject } from "../../../api/src/lib";
import type { Transaction } from "../../../api/src/transactions/lib";
import Loading from "../Loading";
import { NotificationContext } from "../Notifications";
import { deserializeDate } from "../lib/api";
import { naLabel } from "../lib/display";
import { formatCurrency } from "../lib/numbers";
import { useAuthenticatedFetchAllPages } from "../lib/pagination";
import SecurityLink from "./SecurityLink";
import { Table, useTable } from "./Table/Table";
import {
  TransactionSubTypeFilter,
  TransactionTypeFilter,
  multiSelectIncludes,
  numberStringFilter,
  transactionSubTypeOptions,
  transactionTypeOptions,
} from "./Table/filters";

export function deserializeTransaction(
  transaction: SerializedObject<Transaction>,
): Transaction {
  return {
    ...transaction,
    transactionDate: deserializeDate(transaction.transactionDate),
    reportedDate: deserializeDate(transaction.reportedDate),
    settleDate: deserializeDate(transaction.settleDate),
  };
}

/**
 * Every transaction of one of the three `/v1/transactions` lists, deserialized.
 *
 * Each list answers a page (APP-1652), so the walk in
 * `useAuthenticatedFetchAllPages` keeps asking until it holds as many
 * transactions as `pagination.totalCount` reports. `data` stays `undefined`
 * until it does, so the table never shows part of the list. A screen whose
 * `path` can change while it stays mounted remounts this hook on the change
 * (a React `key`), as `lib/pagination.ts` requires.
 */
export function useAllTransactions(path: string) {
  const { rows, hasEveryRow, isError, retry, reload } =
    useAuthenticatedFetchAllPages<SerializedObject<Transaction>>(path);

  const data = useMemo(
    () => (hasEveryRow ? rows.map(deserializeTransaction) : undefined),
    [hasEveryRow, rows],
  );

  // "Try again" sends the read again from the first page. With nothing
  // collected the failed request IS the first page, and `reload` alone would
  // change no query key, so it is asked again. With pages collected, `reload`
  // moves the walk back to offset 0.
  const retryFromFirstPage = useCallback(() => {
    if (rows.length === 0) retry();
    else reload();
  }, [rows.length, retry, reload]);

  return {
    isPending: !hasEveryRow && !isError,
    isError,
    data,
    retry: retryFromFirstPage,
  };
}

const typeLabelToValue = Object.fromEntries(
  transactionTypeOptions.map((option) => [
    option.label,
    option.value.toString(),
  ]),
);
const typeValueToLabel = Object.fromEntries(
  transactionTypeOptions.map((option) => [
    option.value.toString(),
    option.label,
  ]),
);

const subTypeLabelToValue = Object.fromEntries(
  transactionSubTypeOptions.map((option) => [
    option.label,
    option.value.toString(),
  ]),
);
const subTypeValueToLabel = Object.fromEntries(
  transactionSubTypeOptions.map((option) => [
    option.value.toString(),
    option.label,
  ]),
);

type TransactionRow = Transaction;

/** What the table shows when the advisor selects "Export". */
export type TransactionsExportState = {
  excludeSweep: boolean;
  /** The text of each column filter, or the labels of a Type / Sub Type one. */
  filters: {
    household?: string;
    type: string[];
    subType: string[];
    symbol?: string;
    description?: string;
  };
};

const textFilterOf = (value: unknown) =>
  typeof value === "string" && value !== "" ? value : undefined;

const labelsOf = (value: unknown, valueToLabel: Record<string, string>) =>
  Array.isArray(value)
    ? value.flatMap((v) => {
        const label = valueToLabel[String(v)];
        return label === undefined ? [] : [label];
      })
    : [];

const TransactionsTable = ({
  additionalColumns = [],
  isLoading,
  isError,
  onRetry,
  transactions,
  queryExport,
  onExport: onExportWithState,
  excludeCashSweepByDefault = true,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  additionalColumns?: ColumnDef<Transaction, any>[];
  isLoading: boolean;
  isError: boolean;
  /** Sends the read again. Without it the error shows no button. */
  onRetry?: () => void;
  transactions: Transaction[];
  queryExport?: UseQueryResult<void, Error>;
  /**
   * Sends the export with the state of the switch and the column filters, and
   * resolves with its result. Without it, Export refetches `queryExport` as is.
   */
  onExport?: (
    state: TransactionsExportState,
  ) => Promise<{ isError: boolean } | undefined>;
  /** Whether the "Exclude Cash Sweep Transactions" switch starts on. */
  excludeCashSweepByDefault?: boolean;
}) => {
  const columnHelper = useMemo(() => createColumnHelper<TransactionRow>(), []);

  const columns = useMemo(
    () => [
      columnHelper.accessor("transactionDate", {
        cell: (info) => info.getValue().toLocaleDateString(),
        header: () => "Transaction Date",
        enableColumnFilter: false,
      }),
      columnHelper.accessor(
        (row) => {
          return typeLabelToValue[row.type] ?? "";
        },
        {
          id: "type",
          cell: (info) => {
            return typeValueToLabel[info.getValue()] ?? "";
          },
          header: () => "Type",
          filterFn: multiSelectIncludes,
          meta: {
            filterComponent: TransactionTypeFilter,
          },
        },
      ),
      columnHelper.accessor(
        (row) => {
          return subTypeLabelToValue[row.subType] ?? "";
        },
        {
          id: "subType",
          cell: (info) => {
            return subTypeValueToLabel[info.getValue()] ?? "";
          },
          header: () => "Sub Type",
          filterFn: multiSelectIncludes,
          meta: {
            filterComponent: TransactionSubTypeFilter,
          },
        },
      ),
      columnHelper.accessor("amount", {
        cell: (info) => {
          const val = info.getValue();
          return val === null ? naLabel : formatCurrency(val, 2);
        },
        header: () => "Amount",
        filterFn: numberStringFilter,
        enableColumnFilter: false,
        meta: {
          className: "text-end",
          headerClassName: "text-end",
        },
      }),
      columnHelper.accessor("securitySymbol", {
        cell: (info) => <SecurityLink symbol={info.getValue() ?? ""} />,
        header: () => "Security Symbol",
      }),
      columnHelper.accessor("unitPrice", {
        cell: (info) => {
          const val = info.getValue();
          return val === null ? naLabel : formatCurrency(val, 2);
        },
        header: () => "Unit Price",
        enableColumnFilter: false,
        meta: {
          className: "text-end",
          headerClassName: "text-end",
        },
      }),
      columnHelper.accessor("units", {
        cell: (info) => {
          const val = info.getValue();
          return val === null
            ? naLabel
            : val.toLocaleString(undefined, {
                maximumFractionDigits: 1,
              });
        },
        header: () => "Units",
        enableColumnFilter: false,
        meta: {
          className: "text-end",
          headerClassName: "text-end",
        },
      }),
      // `description` is `null` when the custodian gives none; "" keeps the
      // cell empty and the column filtered as text.
      columnHelper.accessor((row) => row.description ?? "", {
        id: "description",
        header: () => "Description",
      }),
    ],
    [columnHelper],
  );

  const [excludeCashSweep, setExcludeCashSweep] = useState(
    excludeCashSweepByDefault,
  );

  const handleCheckedChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setExcludeCashSweep(e.target.checked),
    [],
  );

  const { table } = useTable({
    columns: [columns[0], ...additionalColumns, ...columns.slice(1)],
    data: transactions.filter(
      (transaction) => !excludeCashSweep || transaction.subType !== "Sweep",
    ),
    initialState: {
      sorting: [{ id: "transactionDate", desc: true }],
    },
    getRowId: (row) => row.id.toString(),
    autoResetAll: false,
  });

  const { pushNotification } = useContext(NotificationContext);
  // `refetch()` resolves with the failed result and does not throw, so the
  // error is read from the result: one toast for each failed export.
  const onExport = useCallback(async () => {
    let result: { isError: boolean } | undefined;
    if (onExportWithState) {
      const filterOf = (id: string) =>
        table.getState().columnFilters.find((f) => f.id === id)?.value;
      result = await onExportWithState({
        excludeSweep: excludeCashSweep,
        filters: {
          household: textFilterOf(filterOf("householdName")),
          type: labelsOf(filterOf("type"), typeValueToLabel),
          subType: labelsOf(filterOf("subType"), subTypeValueToLabel),
          symbol: textFilterOf(filterOf("securitySymbol")),
          description: textFilterOf(filterOf("description")),
        },
      });
    } else {
      result = await queryExport?.refetch();
    }
    if (result?.isError) {
      pushNotification({
        id: "transactions-export-failed",
        header: "Failed to export transactions",
        body: null,
        variant: "danger",
      });
    }
  }, [
    queryExport,
    onExportWithState,
    excludeCashSweep,
    table,
    pushNotification,
  ]);

  return isLoading ? (
    <Loading message="Transactions" />
  ) : isError ? (
    <Alert variant="danger">
      An error occurred
      {onRetry && (
        <Button
          variant="outline-danger"
          size="sm"
          className="ms-3"
          onClick={onRetry}
        >
          Try again
        </Button>
      )}
    </Alert>
  ) : (
    <>
      <Row>
        <Col className="d-flex justify-content-start align-items-center gap-3 mb-3">
          <Form.Check
            type="switch"
            label="Exclude Cash Sweep Transactions"
            checked={excludeCashSweep}
            onChange={handleCheckedChange}
          />
          {typeof queryExport === "undefined" ? null : (
            <ButtonToolbar>
              <ButtonGroup>
                <Button
                  type="button"
                  onClick={onExport}
                  disabled={queryExport.isFetching}
                >
                  Export
                </Button>
              </ButtonGroup>
            </ButtonToolbar>
          )}
        </Col>
      </Row>
      <Row>
        <Col>
          <Table table={table} />
        </Col>
      </Row>
    </>
  );
};

export default TransactionsTable;
